import {
	type ClientPropertyArtDetailResponseDTO,
	type ClientPropertyCottageDetailResponseDTO,
	type ClientPropertyFlatDetailResponseDTO,
	type ClientPropertyHouseDetailResponseDTO,
	type ClientPropertyLandDetailResponseDTO,
	ClientPropertyTypeEnum,
	type ClientPropertyVehicleDetailResponseDTO,
	type PortfolioDetailResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";
import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import { propertyCarTitle } from "web_myplann/Portfolio/Helpers/utils";

type PropertyType =
	| ClientPropertyVehicleDetailResponseDTO
	| ClientPropertyHouseDetailResponseDTO
	| ClientPropertyFlatDetailResponseDTO
	| ClientPropertyCottageDetailResponseDTO
	| ClientPropertyLandDetailResponseDTO
	| ClientPropertyArtDetailResponseDTO;

const isVehicle = (
	prop: PropertyType,
): prop is ClientPropertyVehicleDetailResponseDTO => {
	return prop.type === ClientPropertyTypeEnum.AutoMoto;
};

const isArt = (
	prop: PropertyType,
): prop is ClientPropertyArtDetailResponseDTO => {
	return prop.type === ClientPropertyTypeEnum.Art;
};

const isProperty = (
	prop: PropertyType,
): prop is
	| ClientPropertyHouseDetailResponseDTO
	| ClientPropertyFlatDetailResponseDTO
	| ClientPropertyCottageDetailResponseDTO
	| ClientPropertyLandDetailResponseDTO => {
	return (
		prop.type === ClientPropertyTypeEnum.House ||
		prop.type === ClientPropertyTypeEnum.Flat ||
		prop.type === ClientPropertyTypeEnum.Cottage ||
		prop.type === ClientPropertyTypeEnum.Land
	);
};

export const usePortfolioTitle = (
	portfolio: PortfolioDetailResponseDTO | undefined,
	property: PropertyType | undefined,
	t: (text: string) => string,
) => {
	if (!portfolio && !property) return "";

	if (portfolio) {
		const title =
			portfolio.name ??
			[
				portfolio.product,
				portfolio.contractNumber && `(${portfolio.contractNumber})`,
			]
				.filter(Boolean)
				.join(" ");

		if (title) {
			if (title.startsWith("(") && title.endsWith(")")) {
				return title.slice(1, -1);
			}

			return title;
		}

		return portfolio.domain
			? t(Resources.Portfolio.List.PortfolioDomainType[portfolio.domain])
			: "";
	}

	if (property && isVehicle(property)) {
		const title = propertyCarTitle(
			property.carBrand,
			property.carModel,
			property.carLicensePlate,
		);

		if (title) {
			return title;
		}

		return t(Resources.PortfolioDetail.Property.PropertyType[property.type]);
	}

	if (property && isArt(property)) {
		if (property.name) {
			return property.name;
		}

		return `${t(Resources.PortfolioDetail.Property.PropertyType[property.type])} ${property.propertyType ? `(${property.propertyType})` : ""} `;
	}

	if (property && isProperty(property)) {
		if (property.name) {
			return property.name;
		}

		let location = "";

		const parts = [
			property.location,
			property.locationMunicipality,
			property.locationZipCode,
		].filter(Boolean);

		if (parts.length > 0) {
			location = parts.join(", ");
		}

		return `${t(Resources.PortfolioDetail.Property.PropertyType[property.type])} ${location}`;
	}
};
